"use client";

import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowLeft, Plus, Minus } from "lucide-react";
import { useAuth } from "@/lib/auth/auth-context";
import UserMenu from "@/components/auth/UserMenu";
import ThemeToggle from "@/components/ThemeToggle";
import BrandMark from "@/components/BrandMark";
import LocaleSwitcher from "@/components/site/LocaleSwitcher";
import { useT } from "@/i18n/context";

interface NavLink {
  id: string;
  label: string;
  href: string;
  to?: string;
}

interface Props {
  hashMode?: boolean;
  links?: { id: string; label: string }[];
}

const ROUTE_LINK_IDS = [
  { id: "features", key: "common.nav.features", fallback: "امکانات", to: "/features" },
  { id: "solutions", key: "common.nav.solutions", fallback: "راهکارها", to: "/solutions" },
  { id: "trendi", key: "common.nav.ai", fallback: "هوش مصنوعی", to: "/trendi" },
  { id: "pricing", key: "common.nav.pricing", fallback: "تعرفه‌ها", to: "/pricing" },
  { id: "customers", key: "common.nav.customers", fallback: "مشتریان", to: "/customers" },
  { id: "contact", key: "common.nav.contact", fallback: "تماس با ما", to: "/contact" },
] as const;

export function SiteHeader({ hashMode = true, links }: Props) {
  const { status, isAuthenticated } = useAuth();
  const t = useT();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const resolved: NavLink[] = links
    ? links.map((l) => ({ ...l, href: hashMode ? `#${l.id}` : `/#${l.id}` }))
    : ROUTE_LINK_IDS.map((l) => ({ id: l.id, label: t(l.key, l.fallback), href: l.to, to: l.to }));

  const [active, setActive] = useState(resolved[0]?.id ?? "");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="fixed top-0 left-0 right-0 z-50"
    >
      <div
        className={`relative w-full transition-all duration-500 ${
          scrolled
            ? "bg-background/85 backdrop-blur-2xl border-b border-border shadow-[0_8px_32px_-12px_rgba(0,0,0,0.15)]"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        {scrolled && (
          <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-primary-main/40 to-transparent" />
        )}

        <div className="max-w-7xl mx-auto flex items-center justify-between gap-6 px-6 h-16">
          <Link href="/" className="relative flex items-center gap-3 group flex-shrink-0">
            <BrandMark size={38} />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-black text-[15px] tracking-tight text-foreground">
                {t("common.brandName", "ترندی اسمارت")}
              </span>
              <span className="text-[9px] text-primary-main mt-1 tracking-[0.25em] font-bold">
                {t("common.brandTagline", "TRENDI · SMART")}
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {resolved.map((l) => {
              const isActive = active === l.id;
              const cls = `relative px-4 py-2 text-[13.5px] font-bold transition-colors ${
                isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              }`;
              const inner = (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute bottom-0 left-3 right-3 h-[2px] bg-gradient-to-r from-primary-main to-secondary-main rounded-full"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </>
              );
              return l.to ? (
                <Link key={l.id} href={l.to} onMouseEnter={() => setActive(l.id)} className={cls}>
                  {inner}
                </Link>
              ) : (
                <a key={l.id} href={l.href} onMouseEnter={() => setActive(l.id)} className={cls}>
                  {inner}
                </a>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            <ThemeToggle variant="floating" />
            <LocaleSwitcher className="border-border bg-muted text-foreground hover:border-primary-main/40" />
            <div className="hidden md:block w-px h-6 bg-border" />
            {status === "loading" ? (
              <div className="hidden md:block h-10 w-10 rounded-xl bg-muted animate-pulse" />
            ) : isAuthenticated ? (
              <UserMenu />
            ) : (
              <>
                <Link
                  href="/auth"
                  className="hidden md:inline-flex text-[13.5px] font-bold text-muted-foreground hover:text-foreground px-3 py-2 transition-colors"
                >
                  {t("common.auth.login", "ورود")}
                </Link>
                <Link
                  href="/auth"
                  className="group relative inline-flex items-center gap-1.5 bg-primary-main hover:bg-primary-400 text-white px-4 py-2 rounded-lg text-[13px] font-black overflow-hidden shadow-lg shadow-primary-main/30 transition-colors"
                >
                  <span>{t("common.auth.startFree", "شروع رایگان")}</span>
                  <ArrowLeft
                    size={14}
                    className="group-hover:-translate-x-0.5 transition-transform"
                  />
                </Link>
              </>
            )}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-10 h-10 rounded-lg bg-muted border border-border flex items-center justify-center text-foreground"
              aria-label={t("common.menu", "منو")}
            >
              <motion.div animate={{ rotate: mobileOpen ? 90 : 0 }}>
                {mobileOpen ? <Minus size={18} /> : <Plus size={18} />}
              </motion.div>
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mx-3 mt-2 rounded-2xl bg-background/95 backdrop-blur-2xl border border-border p-3 grid grid-cols-2 gap-2 shadow-2xl"
          >
            {resolved.map((l) => (
              <a
                key={l.id}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center justify-between px-4 py-3 rounded-xl bg-muted border border-border text-sm font-bold text-foreground hover:bg-muted/70 hover:border-primary-main/30"
              >
                {l.label}
                <ArrowLeft size={14} className="text-primary-main" />
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export default SiteHeader;
